import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { Device } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCaps } from '../../lib/session';
import { Badge } from '../../components/ui';
import { BASE, SrBuildingTotals, keys } from './api';

/** True when this device row is the company's link on a shared kiosk (C-SR2 adds `shared` to Device). */
export const isSharedLink = (d: Pick<Device, 'shared'>): boolean => Boolean(d.shared);

/**
 * Devices-page marker: "Shared: <building>" with a one-line explanation of the limited actions and a link to the settings page.
 * Used by the devices list (the one slot added there) and safe to use on the device detail page.
 */
export function SharedLinkBadge({ device }: { device: Pick<Device, 'shared'> }) {
  if (!device.shared) return null;
  return (
    <div className="mt-1">
      <Badge tone="neutral">{t('srt.devices.badge', { building: device.shared.buildingName })}</Badge>
      {device.shared.approval === 'pending' ? (
        <p role="status" className="mt-1 max-w-xs rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-950">
          {t('srt.devices.waiting')}
        </p>
      ) : null}
      <p className="mt-1 max-w-xs text-xs text-slate-600">
        {t('srt.devices.limited')}{' '}
        <Link className="font-medium text-brand underline" to="/manage/shared-reception">
          {t('srt.devices.openPage')}
        </Link>
      </p>
    </div>
  );
}

/**
 * Building totals for a company's evacuation screen (setting `buildingTotalsOnEvacuation`; the server answers 404 when no
 * building evacuation is running at this site, so nothing shows). Counts only, never other companies' names.
 */
export function BuildingTotalsBanner({ siteId }: { siteId: string | null }) {
  const caps = useCaps();
  const moduleOn = caps.modules.some((m) => m.key === 'shared-reception' && m.enabled);
  const q = useQuery({
    queryKey: keys.totals(siteId ?? ''),
    queryFn: () => api.get(`${BASE}/evacuations/active`, { query: { siteId }, schema: SrBuildingTotals }),
    enabled: moduleOn && siteId !== null,
    retry: false,
    refetchInterval: 15_000,
  });
  if (!q.data) return null;
  const { totals } = q.data;
  return (
    <section aria-label={t('srt.totals.title', { building: q.data.buildingName })} className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4">
      <h2 className="text-base font-semibold text-slate-900">{t('srt.totals.title', { building: q.data.buildingName })}</h2>
      <p className="mt-1 text-sm text-slate-700">{t('srt.totals.body')}</p>
      <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm font-medium text-slate-900">
        <li>{t('srt.totals.unaccounted', { count: totals.unaccounted })}</li>
        <li>{t('srt.totals.safe', { count: totals.safe })}</li>
        <li>{t('srt.totals.missing', { count: totals.missing })}</li>
        <li>{t('srt.totals.offSite', { count: totals.offSite })}</li>
      </ul>
    </section>
  );
}
